import { z } from "zod";

/** As categorias da base de serviços do laboratório, na ordem em que aparecem. Ficam em
 *  `ServiceCategory.group`; o laboratório pode criar outras ao adicionar um serviço próprio. */
export const CATEGORIAS_DE_SERVICO = [
  "Próteses fixas sobre dentes",
  "Restaurações indiretas e odontologia estética",
  "Próteses sobre implantes",
  "Próteses totais removíveis",
  "Próteses parciais removíveis (PPR)",
  "Placas oclusais e dispositivos terapêuticos",
  "Ortodontia e contenções",
  "Modelos odontológicos",
  "Guias cirúrgicos e planejamento",
  "Provisórios e temporários",
  "Odontologia digital – CAD/CAM",
  "Impressão 3D odontológica",
  "Fresagem e usinagem odontológica",
  "Reparos, manutenção e acabamento",
  "Próteses bucomaxilofaciais e trabalhos especiais",
] as const;

/** Serviço sem categoria (próprio antigo, ou de antes das categorias) — sempre por último. */
export const CATEGORIA_OUTROS = "Outros";

/** Se o pedido marca dentes ou a arcada. Serviço de arcada já vem com as duas marcadas. */
export const abrangenciaDoServicoSchema = z.enum(["DENTE", "ARCADA"]);
export type AbrangenciaDoServico = z.infer<typeof abrangenciaDoServicoSchema>;

export const categoriaDoServicoSchema = z.string().trim().min(1, "Escolha a categoria").max(60);

/** Posição da categoria: as da base na ordem acima, as criadas pelo laboratório depois (por
 *  nome), "Outros" no fim. */
function pesoDaCategoria(nome: string): [number, string] {
  const i = (CATEGORIAS_DE_SERVICO as readonly string[]).indexOf(nome);
  if (i >= 0) return [i, ""];
  if (nome === CATEGORIA_OUTROS) return [CATEGORIAS_DE_SERVICO.length + 1, ""];
  return [CATEGORIAS_DE_SERVICO.length, nome.toLocaleLowerCase("pt-BR")];
}

export function compararCategorias(a: string, b: string): number {
  const [pa, na] = pesoDaCategoria(a);
  const [pb, nb] = pesoDaCategoria(b);
  return pa - pb || na.localeCompare(nb, "pt-BR");
}

/** Agrupa serviços pela categoria, na ordem das categorias; dentro de cada uma, mantém a ordem
 *  em que os serviços chegaram (o servidor já manda por `sortOrder`). */
export function agruparPorCategoria<T extends { group: string | null }>(
  servicos: T[],
): { categoria: string; itens: T[] }[] {
  const grupos = new Map<string, T[]>();
  for (const s of servicos) {
    const categoria = s.group?.trim() || CATEGORIA_OUTROS;
    grupos.set(categoria, [...(grupos.get(categoria) ?? []), s]);
  }
  return [...grupos.entries()]
    .sort(([a], [b]) => compararCategorias(a, b))
    .map(([categoria, itens]) => ({ categoria, itens }));
}
